// React
import * as React from "react";
import _ from 'lodash';
import moment from 'moment';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { TimeSeriesSavedDataChartProps, TSChartType, TSViewType } from "./TimeSeriesSavedDataStructs";
import TSSavedDataChartSettings from "./TimeSeriesSavedDataChartSettings";
import { ExcelTimezone, ExcelExportFormat } from "./TimeSeriesStructs";
import { TimeSeriesChartJsonSchemeValidator } from './TimeSeriesChartJsonScheme';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { DateFormat, Formatters } from '@Utils/Formats';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';

// Async 
const TimeSeriesSavedDataChart = React.lazy(() => import("./TimeSeriesSavedDataChartAsync"));

// =======================================================================================================
// Switch Description interface

const enum PortKeys {
    Data = "data",
    SelectedRange = "SelectedRange",
    onToolbarIconClick = 'onToolbarIconClick'
}
// ------------------------------------------------------------------------------------------------------
const enum TimeSeriesChartKeys {
    onToolbarIconClick = 'onToolbarIconClick'
};
export class TimeSeriesSavedDataChartWidget extends IWidgetThingsClass<TimeSeriesSavedDataChartProps> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget>
            <TimeSeriesSavedDataChart />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <TSSavedDataChartSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Calculate the range
        const now = moment().locale('en-gb').utcOffset(0);
        let minUtcOffset = now.clone().add(-wData.initialTimeRangeDays, 'days');
        let maxUtcOffset = now.clone().add(14, 'hours');
        let dates = Formatters.datetimeRange(minUtcOffset.toDate(), maxUtcOffset.toDate(), DateFormat.ISO, false).split(' - ');

        // Ports
        let ports: WebThingPort[] = [
            new WebThingPort(
                {
                    Name: "Data",
                    PortKey: PortKeys.Data,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "",
                    State: ""
                },
                WebThingPortType.Data,
                (data) => this.ValidateValues(PortKeys.Data, data)
            ),
            new WebThingPort(
                {
                    Name: 'Selected Range',
                    PortKey: PortKeys.SelectedRange,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'The selected range of start and end date',
                    State: JSON.stringify({ StartDate: dates[0], EndDate: dates[1] })
                },
                WebThingPortType.Data
            ),
            new WebThingPort({
                Name: 'onToolbarIconClick',
                PortKey: TimeSeriesChartKeys.onToolbarIconClick,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            }, WebThingPortType.Data)

        ];

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TimeSeriesSavedDataChartProps {
        return {
            chartType: TSChartType.line,
            showGridLines: false,
            showStats: false,
            maxColor: 'red',
            minColor: 'blue',
            showTimeRange: true,
            initialTimeRangeDays: 30,
            excelDownload: false,
            excelExportFormat: ExcelExportFormat.Both,
            excelOptions: {
                fileName: "Data",
                datetimeFormat: "MM-DD-YY hh:mm",
                timezone: ExcelTimezone.UTC,
                useNativeDatetime: true,
            },
            customToolbarIcons: [],
            toolbarHeight: 25,
            toolbarIconSize: 18,
            ShowMarkersOnHover: false,
            showValueList: true,
            showFloatingLabels: false,
            showOnlyCurrentPointFloatingLabel: false,
            ViewType: TSViewType.none,
            commonCharts: true,
            showEdit: true,
            showReset: true,
            showCommonChart: true,
            floatingInfoLabelsFontColor: 'gray'
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return TimeSeriesSavedDataChartWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: TimeSeriesSavedDataChartProps = TimeSeriesSavedDataChartWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Time Series (saved data)",
            WidgetTitle: "",
            Key: "TimeSeriesSavedDataChartWidget",
            Type: "TimeSeriesSavedDataChartWidget",
            Data: data,
            Description: "Time Series Chart Widget (saved data)",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/timeseries_saved_data.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    GetDefaultValues(): { [key: string]: any } {
        return {};
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.Data)
            return TimeSeriesChartJsonSchemeValidator().DataValidateString(value);
        return { valid: true, errors: '' };
    };
    // ------------------------------------------------------------------------------------------------
    public static MigrateDefaultWidgetDataStatic(data: TimeSeriesSavedDataChartProps): TimeSeriesSavedDataChartProps {

        // handle 'showPoints' removal
        const showPoints = (data as any).showPoints;
        if (isDefined(showPoints)) {
            delete (data as any).showPoints;
            if (showPoints === true && data.chartType == TSChartType.line)
                data.chartType = TSChartType.lineWithPoints;
        }

        // handle autoValueRange
        if (isDefined((data as any).autoValueRange)) {
            if ((data as any).autoValueRange === true)
                data.ViewType = TSViewType.autoValueRange;
            else
                data.ViewType = TSViewType.none;

            delete (data as any).autoValueRange;
        }

        // rename downloadExcel to excelDownload
        if (data.excelDownload == null)
            data.excelDownload = (data as any).downloadExcel;

        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: TimeSeriesSavedDataChartProps): TimeSeriesSavedDataChartProps {
        return TimeSeriesSavedDataChartWidget.MigrateDefaultWidgetDataStatic(data);
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    }
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================